import { DateTime } from 'luxon'

import { capitalizeString, digObject } from '@domain-group/javascript-utils'

import type { EntitiesState } from '@redux/entities'

import formatDate, { formatToDateTime } from '@functions/formatDate'

import type { PublicationEditionModel } from '@models/types'

import { PublicationEditionDeadline } from '@hooks/usePublicationEditionDeadlines'

import { formatRelativeDate } from './formatRelativeDate'

export type SecondaryGroupModel = {
  id: number,
  entityIds: number[],
}

type SecondaryGroupingKey = 'agencies' | 'brandGroups'

type SecondaryGrouping = { [key in SecondaryGroupingKey]: { [key: number]: SecondaryGroupModel } }

export type GroupedEditorialModel = SecondaryGrouping & {
  id: number,
  entityCount: number,
  publicationDate: string,
}

export type GroupedEditorialsModel = {
  [key: number]: GroupedEditorialModel,
}

const sortGroupedEntitiesByEdition = (groupedEditorials: GroupedEditorialsModel) => {
  const array = Object.values(groupedEditorials)

  return array.sort((a, b) => {
    const sortA = DateTime.fromFormat(a.publicationDate, 'dd/MM/yyyy')
    const sortB = DateTime.fromFormat(b.publicationDate, 'dd/MM/yyyy')

    if (sortA > sortB) return -1
    if (sortA < sortB) return 1
    return 0
  })
}

type Entity = {
  [key: string]: any,
}

type groupEntitiesByEditionParams = {
  callbacks: {
    getPrimaryGroupId: (entity: Entity) => number,
    getSecondaryGroupId: (entity: Entity) => number,
  },
  entities: Entity[],
  publicationEditions: EntitiesState['publicationEditions'],
  secondaryGroupKey: SecondaryGroupingKey,
}

export const groupEntitiesByEdition = (params: groupEntitiesByEditionParams) => {
  const {
    callbacks: {
      getPrimaryGroupId,
      getSecondaryGroupId,
    },
    entities,
    publicationEditions,
    secondaryGroupKey,
  } = params

  const grouped: GroupedEditorialsModel = {}

  entities.forEach((entity) => {
    const editionId = getPrimaryGroupId(entity)
    const secondaryGroupId = getSecondaryGroupId(entity)

    const edition = publicationEditions[editionId]

    if (edition){
      // Add Edition to Group
      if (!grouped[editionId]){
        // @ts-expect-error TS2322
        grouped[editionId] = {
          id: edition.id,
          publicationDate: edition.dates.publication.date,
          [secondaryGroupKey]: {},
          entityCount: 0,
        }
      }

      // Add Secondary Entity
      if (!grouped[editionId][secondaryGroupKey]![secondaryGroupId]){
        grouped[editionId][secondaryGroupKey]![secondaryGroupId] = {
          id: secondaryGroupId,
          entityIds: [],
        }
      }

      // Add Booking
      grouped[editionId][secondaryGroupKey]![secondaryGroupId].entityIds.push(entity.id)

      // Increment Counts
      grouped[editionId].entityCount += 1
    }
  })

  return sortGroupedEntitiesByEdition(grouped)
}

// @ts-expect-error TS2740
export const mapPublicationEditionToModel = (publicationEdition: PublicationEditionModel = {}) => {
  const artworkDeadline = formatToDateTime(publicationEdition.dates?.artwork?.date_time)
  const bookingDeadline = formatToDateTime(publicationEdition.dates?.booking?.date_time)
  const editorialDeadline = formatToDateTime(publicationEdition.dates?.editorial?.date_time)
  const inspectionDeadline = formatToDateTime(publicationEdition.dates?.inspection?.date_time)
  const publicationDate = formatDate(publicationEdition?.publication_date, 'dd/MM/yyyy')

  const mappedPublicationEdition = {
    ...publicationEdition,
    // @ts-expect-error TS18047
    artwork_deadline: artworkDeadline.substring(0, 16),
    // @ts-expect-error TS18047
    booking_deadline: bookingDeadline.substring(0, 16),
    // @ts-expect-error TS18047
    editorial_deadline: editorialDeadline.substring(0, 16),
    // @ts-expect-error TS18047
    inspection_deadline: inspectionDeadline.substring(0, 16),
    publication_date: publicationDate,
  }

  return mappedPublicationEdition
}

export const formatEditionDeadlines = (edition: PublicationEditionModel) => {
  const {
    deadlines = {}, id, order_item_count, publication_date, publication_id, special_deadline,
  } = edition

  const deadlinesArray: PublicationEditionDeadline[] = []

  Object.entries(deadlines).forEach(([key, value]) => {
    if (key !== 'next_up'){
      deadlinesArray.push({
        deadlineType: key,
        // @ts-expect-error TS2322
        deadlineDate: value ? DateTime.fromFormat(value, 'dd/MM/yyyy HH:mm') : null,
        // @ts-expect-error TS2322
        orderItemCount: order_item_count,
        publicationDate: DateTime.fromFormat(publication_date, 'dd/MM/yyyy'),
        publicationId: publication_id,
        publicationEditionId: id,
        title: key === 'ofi' ? 'OFI' : capitalizeString(key),
        // @ts-expect-error TS2322
        specialDeadline: special_deadline,
        // @ts-expect-error TS2322
        relativeDate: value ? formatRelativeDate(value) : null,
      })
    }
  })

  return deadlinesArray
}

type CheckEditionValidParams = {
  dateNow?: object,
  publicationEditionId?: number,
  publicationEdition?: PublicationEditionModel,
}

export const checkEditionValid = (params: CheckEditionValidParams) => {
  const { dateNow = DateTime.now(), publicationEditionId, publicationEdition = {} } = params

  if (publicationEditionId){
    const publicationDateString = digObject(publicationEdition, 'dates.publication.date', undefined)
    const publicationDate = publicationDateString ? DateTime.fromFormat(publicationDateString, 'dd/MM/yyyy') : undefined

    const bookingDeadlineString = digObject(publicationEdition, 'dates.booking.date_time', undefined)
    const bookingDeadline = bookingDeadlineString
      ? DateTime.fromFormat(bookingDeadlineString, 'dd/MM/yyyy HH:mm')
      : undefined

    const editionDate = bookingDeadline || publicationDate
    const editionPassed = editionDate ? editionDate < dateNow : true

    return !editionPassed
  }

  return true
}

type GetNextDeadlineParams = {
  dateNow?: object,
  publicationEdition: PublicationEditionModel,
}

export const getNextDeadline = (params: GetNextDeadlineParams) => {
  const { dateNow = DateTime.now(), publicationEdition } = params

  const nextUp = digObject(publicationEdition, 'deadlines.next_up')

  if (nextUp){
    const { date, type } = nextUp

    const nextUpDate = DateTime.fromFormat(date, 'dd/MM/yyyy HH:mm')

    return {
      date,
      dateObject: nextUpDate,
      type,
      typeFormatted: type === 'ofi' ? 'OFI' : type[0].toUpperCase() + type.slice(1),
      // @ts-expect-error TS2362
      urgent: (nextUpDate - dateNow) / 60000 < 60,
    }
  }

  return null
}

export function getDeadlineStatuses(publicationEdition: PublicationEditionModel) {
  const { deadlines } = publicationEdition

  const dateNow = DateTime.now()

  const statuses = {
    artwork: {
      beforeDeadline: deadlines?.artwork ? DateTime.fromFormat(deadlines.artwork, 'dd/MM/yyyy HH:mm') > dateNow : false,
    },
    booking: {
      beforeDeadline: deadlines?.booking ? DateTime.fromFormat(deadlines.booking, 'dd/MM/yyyy HH:mm') > dateNow : false,
    },
    editorial: {
      beforeDeadline: deadlines?.editorial ? DateTime.fromFormat(deadlines.editorial, 'dd/MM/yyyy HH:mm') > dateNow : false,
    },
    ofi: {
      beforeDeadline: deadlines?.ofi ? DateTime.fromFormat(deadlines.ofi, 'dd/MM/yyyy HH:mm') > dateNow : false,
    },
  }

  return statuses
}
